import React from "react";
import { Dialog } from "@mui/material";
import { X } from "@phosphor-icons/react";

interface ConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  // Omitted when the modal's body is its `children` alone (a list of what
  // the action touches): no empty paragraph is drawn.
  message?: string;
  confirmText?: string;
  // Pass null to render a single-button (OK-only) acknowledgement modal —
  // confirm and close collapse into one action. Used by ReadOnlyCellModal
  // and other informational popups that don't have an alternative action.
  cancelText?: string | null;
  confirmButtonColor?:
    | "primary"
    | "secondary"
    | "dark"
    | "error"
    | "warning"
    | "info"
    | "success";
  isLoading?: boolean;
  // Blocks confirming while the modal's own precondition is unmet — the
  // checkbox in `children` still unticked. Distinct from `isLoading`, which
  // additionally relabels the button "Processing...".
  confirmDisabled?: boolean;
  // Rendered under the message: a control the confirmation carries with
  // it (a "don't ask again" row), or the list of what the action touches;
  // never a second paragraph of copy.
  children?: React.ReactNode;
  // False leaves closing to the caller, which closes the modal once its
  // work has landed and keeps it open to show a failure.
  closesOnConfirm?: boolean;
  // False drops the ✕ in the header; Cancel, Esc and the scrim remain.
  showClose?: boolean;
  // "cancel" puts the initial focus on Cancel and stops the dialog-wide Enter
  // from confirming, so a keypress carried over from what opened the modal
  // cannot carry out the action.
  initialFocus?: "confirm" | "cancel";
  // The paper's width in px.
  width?: number;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  confirmButtonColor = "primary",
  isLoading = false,
  confirmDisabled = false,
  children,
  closesOnConfirm = true,
  showClose = true,
  initialFocus = "confirm",
  width = 400,
}) => {
  // The one choke point for confirming: the button's `disabled` and the
  // Enter handler below both route here rather than re-testing the guards.
  const handleConfirm = () => {
    if (isLoading || confirmDisabled) return;
    onConfirm();
    if (closesOnConfirm) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      // Enter on any focusable inside the dialog confirms. MUI's
      // focus-trap parks focus on the first focusable element (the
      // close X), so a button-level autoFocus alone doesn't catch
      // Enter — handle it at the dialog scope. Skip when the focus
      // is on a form input that takes its own Enter (textarea,
      // search field, contenteditable) so users can type freely.
      onKeyDown={(e) => {
        if (e.key !== "Enter") return;
        // With the focus on Cancel by design, Enter activates whichever
        // button has the focus and never confirms on the dialog's behalf.
        if (initialFocus === "cancel") return;
        const tgt = e.target as HTMLElement | null;
        // A control the caller put in `children` owns its own Enter. Without
        // this the dialog swallowed Enter while that control gated confirming,
        // so the keypress fell through to native activation and the *next*
        // Enter confirmed — two presses reaching the action past the very
        // control guarding it (#11887).
        if (tgt?.closest("[data-modal-children]")) return;
        // Nothing to confirm yet: leave the keypress alone so it still
        // activates whichever button has focus. Swallowing it here made the
        // first Enter a keyboard user presses a dead key, since MUI parks
        // initial focus on the close X.
        if (isLoading || confirmDisabled) return;
        const tag = tgt?.tagName;
        if (
          tag === "TEXTAREA" ||
          tgt?.getAttribute("contenteditable") === "true"
        )
          return;
        if (tag === "INPUT") {
          const type = (tgt as HTMLInputElement).type;
          if (type === "search" || type === "text" || type === "email") {
            // Plain text input — let the input own Enter unless the
            // input is empty (in which case Enter should still
            // confirm). Match the user's mental model of "Enter on a
            // confirm modal does the action".
            const val = (tgt as HTMLInputElement).value;
            if (val.trim() !== "") return;
          }
        }
        e.preventDefault();
        e.stopPropagation();
        handleConfirm();
      }}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          width: `${width}px`,
          maxWidth: "90vw",
        },
      }}
    >
      {/* Phone gutters below the tier line (rules/frontend.md, Small screens). */}
      <div className="p-4 md:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          {showClose && (
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors duration-200"
              disabled={isLoading}
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="px-0 py-2">
          {message !== undefined && (
            <p className="text-gray-600 text-md leading-relaxed">{message}</p>
          )}
          {children && <div data-modal-children>{children}</div>}
        </div>

        {/* Actions: each label stays on one line, and on a narrow paper the
            buttons wrap to a second row rather than squeeze their labels
            onto two (#14877). */}
        <div className="flex flex-wrap justify-end gap-3 pt-6">
          {cancelText !== null && (
            <button
              onClick={onClose}
              disabled={isLoading}
              autoFocus={initialFocus === "cancel"}
              className="whitespace-nowrap px-6 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={handleConfirm}
            disabled={isLoading || confirmDisabled}
            autoFocus={initialFocus === "confirm"}
            className={`whitespace-nowrap px-6 py-2 rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
              confirmButtonColor === "error"
                ? "bg-red-600 text-white hover:bg-red-700"
                : confirmButtonColor === "warning"
                  ? "bg-yellow-600 text-white hover:bg-yellow-700"
                  : confirmButtonColor === "success"
                    ? "bg-green-600 text-white hover:bg-green-700"
                    : confirmButtonColor === "info"
                      ? "bg-blue-600 text-white hover:bg-blue-700"
                      : confirmButtonColor === "dark"
                        ? "bg-gray-800 text-white hover:bg-gray-700"
                        : confirmButtonColor === "secondary"
                          ? "bg-gray-600 text-white hover:bg-gray-700"
                          : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            {isLoading ? "Processing..." : confirmText}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default ConfirmationModal;
